// app/components/Navbar.tsx
"use client";

import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { motion, useScroll, useSpring, useTransform, MotionValue, AnimatePresence } from "framer-motion";
import { useRouter, usePathname } from "next/navigation";
import Image from "next/image";
import { Playfair_Display, Inter } from "next/font/google";
import { Menu, X } from "lucide-react";

const GREEN = "rgb(34 150 83)";
const BORDER = "rgba(0,0,0,0.10)";

const playfair = Playfair_Display({
  subsets: ["latin"],
  weight: ["600", "700"],
});

const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500"],
});

type NavItem = { label: string; id: string; isRoute?: boolean };

const NAV_ITEMS: NavItem[] = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Treatments", id: "/treatments", isRoute: true },
  { label: "Dentist", id: "dentist" },
  { label: "Gallery", id: "gallery" },
  { label: "Testimonials", id: "testimonials" },
  { label: "Equipments", id: "equipments" },
  { label: "FAQ", id: "faqs" },
  { label: "Blogs", id: "/blogs", isRoute: true },
  { label: "Contact", id: "contact" },
];  

function goTo(
  id: string,
  isRoute: boolean | undefined,
  router: ReturnType<typeof useRouter>,
  pathname: string
) {
  if (isRoute) {
    router.push(id);
    return;
  }

  if (pathname !== "/") {
    router.push(`/#${id}`);
    return;
  }

  const el = document.getElementById(id);
  if (!el) return;

  const header = document.getElementById("site-navbar");
  const offset = header ? header.offsetHeight + 16 : 80;

  const top = el.getBoundingClientRect().top + window.scrollY - offset;

  window.history.pushState(null, "", `#${id}`);

  window.scrollTo({ top, behavior: "smooth" });
}

export default function Navbar() {
  const router = useRouter();
  const pathname = usePathname();

  const { scrollYProgress } = useScroll();

  const bgSpring = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 24,
    mass: 0.3,
  });

  const bgColor = useTransform(
    bgSpring,
    (v: number) => `rgba(255,255,255,${0.65 + Math.min(v * 0.35, 0.35)})`
  );

  const [active, setActive] = useState(0);
  const activeRef = useRef(active);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const [mobileOpen, setMobileOpen] = useState(false);

  // Sync active state with pathname for route-based nav items
  useEffect(() => {
    const routeIdx = NAV_ITEMS.findIndex(
      (n) => n.isRoute && pathname.startsWith(n.id)
    );
    if (routeIdx >= 0) {
      setActive(routeIdx);
      return;
    }
    // On homepage, reset to Home if no section is intersecting yet
    if (pathname === "/") {
      setActive(0);
    }
  }, [pathname]);

  useEffect(() => {
    if (pathname !== "/") return;

    const io = new IntersectionObserver(
      (entries) => {
        let bestIdx = activeRef.current;
        let best = 0;
        for (const e of entries) {
          const idx = NAV_ITEMS.findIndex((n) => n.id === e.target.id);
          if (idx >= 0 && e.isIntersecting && e.intersectionRatio >= best) {
            best = e.intersectionRatio;
            bestIdx = idx;
          }
        }
        if (best > 0 && bestIdx !== activeRef.current) {
          setActive(bestIdx);
        }
      },
      {
        root: null,
        rootMargin: "-20% 0px -60% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    NAV_ITEMS.forEach(({ id, isRoute }) => {
      if (isRoute) return;
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });

    return () => io.disconnect();
  }, [pathname]);

  const railRef = useRef<HTMLUListElement>(null);
  const [underline, setUnderline] = useState({ x: 0, w: 0 });

  const measure = useCallback(() => {
    const buttons =
      railRef.current?.querySelectorAll<HTMLButtonElement>("button[data-nav='btn']");
    if (!buttons || !buttons[active]) return;
    const rect = buttons[active].getBoundingClientRect();
    const parent = railRef.current?.getBoundingClientRect();
    if (!parent) return;
    setUnderline({ x: rect.left - parent.left, w: rect.width });
  }, [active]);

  useLayoutEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (railRef.current) ro.observe(railRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const x = useSpring(underline.x, { stiffness: 600, damping: 36 });
  const w = useSpring(underline.w, { stiffness: 600, damping: 36 });

  return (
    <div className="fixed inset-x-0 top-0 z-50">
      <motion.header
        id="site-navbar"
        className="mx-auto mt-3 w-[92%] rounded-2xl border shadow-[0_10px_30px_rgba(0,0,0,0.06)] backdrop-blur-md"
        style={{
          backgroundColor: bgColor as unknown as MotionValue<string>,
          borderColor: BORDER,
        }}
        role="banner"
      >
        <nav
          className="flex items-center justify-between gap-4 px-6 py-3"
          aria-label="Primary navigation"
        >
          {/* Brand */}
          <button
            type="button"
            onClick={() => router.push("/")}
            className="cursor-pointer focus-visible:ring-2 flex items-center gap-3 rounded-full focus:outline-none"
            aria-label="Pinak Dental Clinic - Dr. Ayush"
          >
            <div className="grid size-11 place-items-center">
              <Image
                src="/logo1.png"
                alt="Pinak Dental Clinic Logo"
                width={46}
                height={46}
                className="object-cover"
                priority
              />
            </div>

            <div className="flex flex-col leading-none">
              <span className={`${playfair.className} text-[22px] tracking-[0.08em] text-gray-900`}>
                Pinak
              </span>
              <span className={`${inter.className} mt-1 text-[11px] uppercase tracking-[0.35em] text-emerald-700`}>
                Dental Clinic
              </span>
            </div>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div
              className="relative overflow-visible rounded-full border bg-white/80 px-3 py-2 backdrop-blur-lg"
              style={{ borderColor: BORDER }}
            >
              <motion.div
                className="pointer-events-none absolute -bottom-[2px] left-0 h-[3px] rounded-full shadow-[0_0_6px_rgba(34,197,94,0.6)]"
                style={{
                  x: x as unknown as MotionValue<number>,
                  width: w as unknown as MotionValue<number>,
                  backgroundColor: GREEN,
                  zIndex: 50,
                }}
                aria-hidden
              />

              <ul ref={railRef} className="relative z-10 flex items-center gap-1" role="menubar">
                {NAV_ITEMS.map(({ label, id, isRoute }, i) => {
                  const isActive = i === active;
                  return (
                    <li key={id} role="none">
                      <motion.button
                        type="button"
                        data-nav="btn"
                        onClick={() => goTo(id, isRoute, router, pathname)}
                        className="cursor-pointer rounded-full px-5 py-2 text-sm font-medium"
                        style={{
                          color: isActive ? "rgb(34 150 83)" : "rgb(107 114 128)",
                        }}
                      >
                        {label}
                      </motion.button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Right Buttons */}
          <div className="flex items-center gap-2">
            <motion.button
              onClick={() => goTo("contact", false, router, pathname)}
              className="hidden md:block rounded-full px-6 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: GREEN }}
            >
              Book Appointment
            </motion.button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2"
            >
              {mobileOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden mx-auto mt-3 w-[92%] rounded-2xl border bg-white shadow-xl"
          >
            <div className="flex flex-col divide-y">
              {NAV_ITEMS.map(({ label, id, isRoute }) => (
                <button
                  key={id}
                  onClick={() => {
                    goTo(id, isRoute, router, pathname);
                    setMobileOpen(false);
                  }}
                  className="py-2 text-left px-6 text-gray-700 hover:bg-gray-50"
                >
                  {label}
                </button>
              ))}

              <button
                onClick={() => {
                  goTo("contact", false, router, pathname);
                  setMobileOpen(false);
                }}
                className="m-4 rounded-xl py-3 text-white font-semibold"
                style={{ backgroundColor: GREEN }}
              >
                Book Appointment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}